/**
 * Admin Route Component
 *
 * Wraps routes that require admin privileges
 * Redirects to unauthorized page if user is not an admin
 */

import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useUserProfile } from '../../hooks/useAuth';
import Loading from '../Loading';

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * AdminRoute component
 * Checks if authenticated user has admin privileges
 * Redirects to /unauthorized if not admin or not authenticated
 *
 * @example
 * <AdminRoute>
 *   <AdminDashboard />
 * </AdminRoute>
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { data: userProfile, isLoading, error } = useUserProfile();

  // Show loading state while fetching user profile
  if (isLoading) {
    return <Loading fullScreen label="Loading..." />;
  }

  // If error fetching profile (not authenticated), redirect to login
  if (error) {
    const currentPath = window.location.pathname;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(currentPath)}`}
        replace
      />
    );
  }

  // If user is not admin, redirect to unauthorized page
  if (!userProfile?.isAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  // User is admin, render the protected content
  return <>{children}</>;
}